import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { PricingResolution, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React from 'react';

import { resolutionLabel } from '~components/Job/LoadPricing/loadPricingHelpers';
import RateCardShell, { StarredRateCardBadge } from '~components/RateCard/RateCardShell';
import RateCardSummary from '~components/RateCard/RateCardSummary';
import {
  buildRateProvenanceGroups,
  RateSourcesByParty,
} from '~components/RateCard/rateProvenance';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { formatRateWithType, toRateNumber } from '~utils/rateFormatting';

/**
 * Wraps a job row's Rates cell with the provenance of the rates in it — which rate card, or
 * which named Rate, each party's rate came from — so a dispatcher can audit a price without
 * opening the edit dialog.
 *
 * Deliberately framed as provenance ("Rates applied from"), never as a claim that the source
 * still matches. `JobService::Update` has no server-side auto-clear of the card stamp when a
 * rate value is edited without an explicit card id, and the FE divergence watcher does not
 * cover the public API or bulk paths, so a stamp can outlive the rate it priced.
 *
 * Renders `children` unwrapped when no party has a resolvable source or pay commission, so
 * rows with no rate history get no empty tooltip.
 */

const PARTY_LABEL_KEYS = {
  customer: 'common.customer',
  driver: 'common.driver',
  vendor: 'common.vendor',
} as const;

// `defaultCard` is absent: the order's starred card reuses `rate_card.applied` — the SAME key
// the order card's popover renders — so the two surfaces cannot drift apart.
const NON_STARRED_SOURCE_LABEL_KEYS = {
  matchedCard: 'rate_card.source_matched_card',
  namedRate: 'rate_card.source_named_rate',
} as const;

// `defaultCard` is absent on purpose: the order's starred card gets the shared brand-yellow
// StarredRateCardBadge instead, so it reads identically here and in the order card's popover —
// they are the same object, and two visual languages read as two different concepts.
const SOURCE_TONE_BACKGROUNDS = {
  matchedCard: (theme: Theme) => theme.semantic.chipInfoBg,
  namedRate: (theme: Theme) => theme.semantic.surfaceSunken,
} as const;

export interface PayCommissionBreakdown {
  grossRateValue: number | null;
  isLocked: boolean;
  netRate: string | null;
  party: 'driver' | 'vendor';
  percentage: number;
  rateType: RateType;
  resolution: PricingResolution | null;
}

interface RateProvenanceTooltipProps {
  /**
   * Each party's rate source. Callers pass all-empty when the viewer is not cleared to see
   * every party's rate — a card exposes all three, so a partial view would leak pricing.
   */
  sources: RateSourcesByParty;
  /** The order's default card, used only to tell a default card from a matched one. */
  defaultRateCardId: string | null;
  payCommissions?: PayCommissionBreakdown[];
  children: React.ReactNode;
}

export default function RateProvenanceTooltip({
  sources,
  defaultRateCardId,
  payCommissions = [],
  children,
}: RateProvenanceTooltipProps) {
  const { currencyFormatter } = useCompanyCurrency();

  const provenanceGroups = buildRateProvenanceGroups({ defaultRateCardId, sources });

  if (provenanceGroups.length === 0 && payCommissions.length === 0) {
    return <>{children}</>;
  }

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {provenanceGroups.length > 0 && (
        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray1,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          })}
        >
          {t('rate_card.applied_from')}
        </Typography>
      )}

      {provenanceGroups.map(({ key, kind, rateCard, rate, parties }) => {
        const isStarredOnOrder = kind === 'defaultCard';

        return (
          <RateCardShell
            key={key}
            badge={
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                }}
              >
                {/* Which parties took their rate from this source. When all three share one
                    source this is the only thing distinguishing it from a single-party group. */}
                <Typography
                  sx={(theme) => ({
                    color: theme.brandV2.colors.treadGray1,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                  })}
                >
                  {parties.map((party) => t(PARTY_LABEL_KEYS[party])).join(' · ')}
                </Typography>

                {isStarredOnOrder ? (
                  <StarredRateCardBadge label={t('rate_card.applied')} />
                ) : (
                  <Typography
                    sx={(theme) => ({
                      backgroundColor: SOURCE_TONE_BACKGROUNDS[kind](theme),
                      borderRadius: '0.25rem',
                      color: theme.semantic.textPrimary,
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.03em',
                      px: 0.5,
                      textTransform: 'uppercase',
                    })}
                  >
                    {t(NON_STARRED_SOURCE_LABEL_KEYS[kind])}
                  </Typography>
                )}
              </Box>
            }
            isStarred={isStarredOnOrder}
          >
            {rateCard ? (
              <RateCardSummary rateCard={rateCard} />
            ) : (
              rate && (
                <Typography
                  sx={(theme) => ({
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    overflowWrap: 'anywhere',
                  })}
                >
                  {rate.name}
                  <Box
                    component="span"
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadGray1,
                      fontWeight: 500,
                      ml: 0.75,
                    })}
                  >
                    {formatRateWithType(rate.rate, rate.type, currencyFormatter, {
                      unitStyle: 'short',
                    })}
                  </Box>
                </Typography>
              )
            )}
          </RateCardShell>
        );
      })}

      {payCommissions.map(
        (
          { grossRateValue, isLocked, netRate, party, percentage, rateType, resolution },
          index,
        ) => {
          const formatCommissionRate = (amount: number | string | null) =>
            toRateNumber(amount) === null
              ? ''
              : formatRateWithType(amount, rateType, currencyFormatter, {
                  unitStyle: 'short',
                });
          const rows = [
            {
              label: t('pay_commission.gross_rate'),
              value: formatCommissionRate(grossRateValue),
            },
            {
              label: t('pay_commission.commission'),
              value: t('pay_commission.deducted_from_gross', { percentage }),
            },
            {
              label: t('pay_commission.actual_rate_label'),
              value: formatCommissionRate(netRate),
            },
            {
              label: t('pay_commission.source'),
              value: resolution ? resolutionLabel(resolution) : '',
            },
          ].filter(({ value }) => value);

          return (
            <Box
              key={party}
              sx={(theme) => ({
                ...(provenanceGroups.length > 0 || index > 0
                  ? { borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`, pt: 1 }
                  : {}),
                columnGap: 1.5,
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                rowGap: 0.25,
              })}
            >
              <Typography
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray1,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  gridColumn: '1 / -1',
                })}
              >
                {t(PARTY_LABEL_KEYS[party])}
              </Typography>
              {rows.map(({ label, value }) => (
                <React.Fragment key={label}>
                  <Typography
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadGray1,
                      fontSize: '0.75rem',
                    })}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: theme.semantic.textPrimary,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    })}
                  >
                    {value}
                  </Typography>
                </React.Fragment>
              ))}
              {isLocked && (
                <Typography
                  sx={(theme) => ({
                    color: theme.brandV2.colors.treadGray1,
                    fontSize: '0.6875rem',
                    fontStyle: 'italic',
                    gridColumn: '1 / -1',
                  })}
                >
                  {t('pay_commission.locked_note')}
                </Typography>
              )}
            </Box>
          );
        },
      )}
    </Box>
  );

  return (
    <Tooltip
      placement="right"
      slotProps={{
        tooltip: {
          // MUI's default tooltip is a dark chip with white text. RateCardSummary paints its
          // own text from the mode-aware ink ramp, which would be unreadable on that fill, so
          // the tooltip is restyled as an app surface panel — the same treatment as the
          // rate-card popover paper.
          sx: (theme) => ({
            backgroundColor: theme.semantic.surface,
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: '0.25rem',
            boxShadow: theme.shadows[3],
            maxWidth: '24rem',
            p: 1.25,
          }),
        },
      }}
      title={tooltipContent}
    >
      {/* Tooltip needs a single ref-holding child; the cell's own layout wrappers are plain
          function components that do not forward refs. */}
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Tooltip>
  );
}
